import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import { Chip, Stack, Typography } from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FacetFilterRail } from "../../../components/UIToolkit/Filters/FacetFilterRail";
import { FilteredEmptyState } from "../../../components/UIToolkit/Filters/FilteredEmptyState";
import { FilterRailFrame } from "../../../components/UIToolkit/Filters/FilterRailFrame";
import type { FacetSpec } from "../../../components/UIToolkit/Filters/facetSections";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../components/UIToolkit/molecules/SettingsSection";
import { SystemDataGrid, type SystemGridColDef } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useFilterRailHandoff, useGridFacetFilters } from "../../../hooks/useFacetFilters";
import type { PbjMissingPunchRow } from "../../../utils/models";
import { usePbjMissingPunches } from "../hooks/usePbjAudit";
import { dataTableSx, sectionCardSx } from "../PredictionTab/colors";
import { useFacetLabels } from "../pbjFacetLabels";
import { dayTypeFacet, flagFacet, monthFacet, textFacet } from "../pbjFacets";
import { boldNameColumn, hoursColumn, monospaceIdColumn, sectionFrameHeight } from "../pbjGridCells";
import { formatAuditDate } from "../pbjGridFormat";
import { pbjSortOptions } from "../pbjGridSorting";

type Props = {
	startDate: string;
	endDate: string;
};

/** Unique grid id — the same (emp, date, job) can repeat across rows, so fold in the array index. */
type MissingPunchGridRow = PbjMissingPunchRow & { __rowId: string; missing: string };

/**
 * Missing Punches audit — shifts missing a clock-in and/or clock-out punch. Migrated to the canonical
 * SystemDataGrid (opaque pinned header, skeleton loader, internal scroll) from a hand-rolled MUI table.
 */
const COLUMN_FACET: Readonly<Record<string, string>> = { jobDescription: "job", date: "month", missing: "missing" };

export const MissingPunchesTable: FC<Props> = ({ startDate, endDate }) => {
	const { t } = useTranslation();
	const { data, isLoading, isError } = usePbjMissingPunches(startDate, endDate);

	const rows = useMemo<MissingPunchGridRow[]>(
		() =>
			(data ?? []).map((r, i) => ({
				...r,
				__rowId: `${i}|${r.empId}|${String(r.date)}|${r.jobDescription}`,
				missing: [r.missingPunchIn && t("pbj_model.audit.missing_punches.flag.in"), r.missingPunchOut && t("pbj_model.audit.missing_punches.flag.out")]
					.filter(Boolean)
					.join(", "),
			})),
		[data, t],
	);

	const columns: SystemGridColDef<MissingPunchGridRow>[] = useMemo(
		() => [
			monospaceIdColumn<MissingPunchGridRow>("empId", t("pbj_model.audit.missing_punches.cols.id")),
			boldNameColumn<MissingPunchGridRow>("name", t("pbj_model.audit.missing_punches.cols.name")),
			{
				field: "jobDescription",
				headerName: t("pbj_model.audit.missing_punches.cols.job_description"),
				minWidth: 160,
				flex: 1,
				valueFormatter: (value: string) => value || "—",
			},
			{
				field: "date",
				headerName: t("pbj_model.audit.missing_punches.cols.date"),
				minWidth: 130,
				flex: 1,
				valueFormatter: (value) => formatAuditDate(value),
			},
			hoursColumn<MissingPunchGridRow>("hours", t("pbj_model.audit.missing_punches.cols.hours"), {
				// A row missing a punch has no computable duration (the BE sends 0), so an em dash is
				// honest where "0.00" would read as a real zero-hour shift.
				valueFormatter: (value: number, row: MissingPunchGridRow) => {
					if (row.missingPunchIn || row.missingPunchOut) return "—";
					return value == null ? "" : value.toFixed(2);
				},
			}),
			{
				field: "missing",
				headerName: t("pbj_model.audit.missing_punches.cols.missing"),
				minWidth: 180,
				flex: 1,
				renderCell: (params: GridRenderCellParams<MissingPunchGridRow>) => {
					const { missingPunchIn, missingPunchOut, empId, date } = params.row;
					const key = `${empId}-${String(date)}`;
					return (
						<Stack direction="row" spacing={0.5} sx={{ alignItems: "center", height: "100%" }}>
							{missingPunchIn ? (
								<Chip
									size="small"
									color="warning"
									variant="outlined"
									icon={<LoginIcon sx={{ fontSize: 14 }} />}
									label={t("pbj_model.audit.missing_punches.flag.in")}
									data-testid={`pbj-model-audit-missing-punches-in-${key}`}
								/>
							) : null}
							{missingPunchOut ? (
								<Chip
									size="small"
									color="warning"
									variant="outlined"
									icon={<LogoutIcon sx={{ fontSize: 14 }} />}
									label={t("pbj_model.audit.missing_punches.flag.out")}
									data-testid={`pbj-model-audit-missing-punches-out-${key}`}
								/>
							) : null}
							{!missingPunchIn && !missingPunchOut ? (
								<Typography variant="body2" color="text.secondary">
									—
								</Typography>
							) : null}
						</Stack>
					);
				},
			},
		],
		[t],
	);

	const labels = useFacetLabels();
	const facets = useMemo<FacetSpec<MissingPunchGridRow>[]>(
		() => [
			{
				dimension: flagFacet<MissingPunchGridRow>("missing", { in: (r) => r.missingPunchIn, out: (r) => r.missingPunchOut }),
				title: t("pbj_model.audit.missing_punches.cols.missing"),
				getLabel: (key) => (key === "in" ? t("pbj_model.audit.missing_punches.flag.in") : t("pbj_model.audit.missing_punches.flag.out")),
				order: ["in", "out"],
			},
			{
				dimension: textFacet<MissingPunchGridRow>("job", (r) => r.jobDescription),
				title: t("pbj_model.audit.missing_punches.cols.job_description"),
				getLabel: labels.raw,
				searchable: true,
			},
			{ dimension: monthFacet<MissingPunchGridRow>("month", (r) => r.date), title: t("pbj_model.filter.month"), getLabel: labels.month, order: "key" },
			{ dimension: dayTypeFacet<MissingPunchGridRow>("day_type", (r) => r.date), title: t("pbj_model.filter.day_type"), getLabel: labels.dayType },
		],
		[t, labels],
	);
	const filters = useGridFacetFilters(rows, facets);
	const railHandoff = useFilterRailHandoff(filters.highlight, COLUMN_FACET, t("pbj_model.filter.in_rail"));

	const sortingOptions = useMemo(
		() =>
			pbjSortOptions<MissingPunchGridRow>([
				{ field: "empId", label: t("pbj_model.audit.missing_punches.cols.id") },
				{ field: "name", label: t("pbj_model.audit.missing_punches.cols.name") },
				{ field: "jobDescription", label: t("pbj_model.audit.missing_punches.cols.job_description") },
				{ field: "date", label: t("pbj_model.audit.missing_punches.cols.date") },
				{ field: "hours", label: t("pbj_model.audit.missing_punches.cols.hours") },
				{ field: "missing", label: t("pbj_model.audit.missing_punches.cols.missing") },
			]),
		[t],
	);

	return (
		<SettingsSection
			layout="stack"
			title={t("pbj_model.audit.missing_punches.section.title")}
			description={t("pbj_model.audit.missing_punches.section.subtitle")}
			dataTestId="pbj-missing-punches-section"
			sx={(theme) => sectionCardSx(theme)}
		>
			<FilterRailFrame
				flat
				height={sectionFrameHeight(rows.length)}
				dataTestId="pbj-missing-punches-filters-frame"
				rail={
					<FacetFilterRail
						rows={rows}
						facets={facets}
						filters={filters}
						collapsed={railHandoff.railCollapsed}
						onCollapsedChange={railHandoff.setRailCollapsed}
						dataTestId="pbj-missing-punches-filters"
					/>
				}
			>
				<SystemDataGrid<MissingPunchGridRow>
					rows={filters.result.rows}
					columns={columns}
					getRowId={(r) => r.__rowId}
					searchableColumns={["empId", "name", "jobDescription"]}
					canFilterByColumn={railHandoff.canFilterByColumn}
					onFilterByColumn={railHandoff.onFilterByColumn}
					filterByHint={railHandoff.filterByHint}
					sortingOptions={sortingOptions}
					showSortSummary
					exportOptions={{ fileName: t("pbj_model.audit.missing_punches.section.title") }}
					searchPlaceholder={t("pbj_model.audit.missing_punches.search")}
					loading={isLoading}
					error={isError}
					errorMessage={t("pbj_model.audit.missing_punches.failed")}
					emptyState={
						<FilteredEmptyState
							filtered={filters.activeCount > 0}
							onClearFilters={filters.clear}
							unfilteredState={<EmptyState title={t("pbj_model.audit.missing_punches.empty")} dataTestId="pbj-model-audit-missing-punches-empty" />}
							dataTestId="pbj-model-audit-missing-punches-filtered-empty"
						/>
					}
					defaultPageSize={25}
					sx={(theme) => dataTableSx(theme)}
					dataTestId="pbj-model-audit-missing-punches"
				/>
			</FilterRailFrame>
		</SettingsSection>
	);
};
